import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import ExitPopupsps from '@/components/ui/ExitPopupsps';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const PayStubGenerator = () => {
  const [referralUrl, SetReferralUrl] = useState('taxzerone');

  useEffect(() => {
    const isIRSPageReferral = document.referrer === 'https://www.irs.gov/';
    if (isIRSPageReferral) SetReferralUrl('taxzerone');
  }, []);

  return (
    <AppLayout
      meta={
        <Meta
          title="Generate US Paystubs Online for FREE with Accurate Taxes"
          description="Tired of manual paystub creation? SecurePayStubs generates professional, error-free paystubs instantly. Get your first one FREE!"
          keywords=""
          canonical="paystub-generator/"
          hideJsonLdSchema="true"
        />
      }
    >
      <ExitPopupsps />
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                Generate Your{' '}
                <Hidden smDown>
                  <br />
                </Hidden>
                <span className="highlight-container">
                  <span className="highlight">Paystubs Online</span>
                </span>{' '}
                with Ease
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                Conveniently create professional paystubs in minutes with our
                secure online{' '}
                <Link
                  href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
                  target="_blank"
                  sx={{ color: '#0202ff' }}
                >
                  paystub generator
                </Link>
                .
              </Typography>

              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box sx={{ textAlign: 'center', mr: { xs: 0, md: 4 } }}>
                  <Button
                    variant="contained"
                    className="btn-primary"
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    href={`${process.env.NEXT_PUBLIC_SPS_APPLICATION_URL}paystub/?ref=${referralUrl}`}
                    target="_blank"
                  >
                    Create a{' '}
                    <Box
                      component="span"
                      sx={{ ml: 1, color: '#eaff00', fontWeight: 700 }}
                    >
                      Paystub
                    </Box>
                    &nbsp;Now
                  </Button>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontSize: { xs: 11, sm: 14 } }}
                  >
                    It take less than 5 minutes
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
          <Hidden mdDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source srcSet="/assets/images/paystub-generator-img.webp" />
                <source srcSet="/assets/images/paystub-generator-img.png" />
                <img
                  src="/assets/images/paystub-generator-img.png"
                  alt="Generate Your Paystubs Online with Ease"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden mdUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source srcSet="/assets/images/paystub-generator-img.webp" />
                <source srcSet="/assets/images/paystub-generator-img.png" />
                <img
                  src="/assets/images/paystub-generator-img.png"
                  alt="Generate Your Paystubs Online with Ease"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
              fontWeight: 600,
              mb: 1,
              textAlign: 'left',
            }}
          >
            What is a paystub generator?
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
          >
            A{' '}
            <Link
              href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
              target="_blank"
              sx={{ color: '#0202ff' }}
            >
              paystub
            </Link>{' '}
            is a document issued by an employer to an employee, accompanying
            their paycheck. It outlines details of the{' '}
            <Link
              href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}employees-paystub/?ref=${referralUrl}`}
              target="_blank"
              sx={{ color: '#0202ff' }}
            >
              employee&apos;s
            </Link>{' '}
            earnings and deductions for a specific pay period, including gross
            wages, taxes withheld, deductions for benefits, and net pay.
          </Typography>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ my: { xs: 2, sm: 6, md: 6 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                How does our paystub generator work?
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
              >
                Generate accurate paystubs in just <b>three simple steps</b>{' '}
                using our online paystub generator.
              </Typography>
            </Grid>
          </Grid>
        </Container>
        <Container maxWidth="xl" sx={{ mb: 5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: 0, md: -2, lg: -5 },
              }}
            >
              <picture>
                <source srcSet="/assets/images/3-steps-img.webp" />
                <source srcSet="/assets/images/3-steps-img.png" />
                <img
                  src="/assets/images/3-steps-img.png"
                  alt="How does our paystub generator work?"
                  className="img-responsive"
                  loading="lazy"
                  width={600}
                  height={440}
                />
              </picture>
            </Box>

            <Box
              sx={{
                width: '100%',
              }}
            >
              <Box
                component="ol"
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                  p: 2,
                  pl: 0,
                  ml: { xs: 0, sm: 0, md: -2 },
                }}
              >
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        my: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-1-icon.webp" />
                        <source srcSet="/assets/images/step-1-icon.png" />
                        <img
                          src="/assets/images/step-1-icon.png"
                          alt="Enter Information"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Enter Information
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                    >
                      Provide details such as your organization’s name, working
                      hours, and salary information.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        m: 0,
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-2-icon.webp" />
                        <source srcSet="/assets/images/step-2-icon.png" />
                        <img
                          src="/assets/images/step-2-icon.png"
                          alt="Choose your paystub template and preview your paystub"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: -1,
                        mb: 1,
                        color: '#36324a',
                        lineHeight: 1.5,
                      }}
                    >
                      Choose a Paystub Template & Preview:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                    >
                      Select your preferred paystub template and preview your
                      salary statement.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-3-icon.webp" />
                        <source srcSet="/assets/images/step-3-icon.png" />
                        <img
                          src="/assets/images/step-3-icon.png"
                          alt="Download and print your stub"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Download & Print Your Paystub:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                    >
                      Instantly download and print your paycheck stub anytime.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              </Box>
            </Box>
          </Stack>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 28, md: 28, sm: 26, xs: 22 },
              fontWeight: 500,
              mt: 5,
              mb: 2,
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            Ready to generate a paystub?
          </Typography>
          <Box
            sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              flexWrap: 'wrap', // stacks on mobile
            }}
          >
            {/* LEFT BUTTON */}
            <Button
              variant="contained"
              sx={{
                transition: 'all 0.3s ease',
                '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                fontSize: '1.1rem !important',
                mr: 1,
              }}
              className="btn-primary"
              href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
              target="_blank"
            >
              Get Started Now
            </Button>

            {/* RIGHT LINK SECTION */}
            <Link
              href="https://youtu.be/CBNo6LYgR7Y"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                }}
              >
                {/* Pulsing Icon */}
                <Box
                  sx={{
                    position: 'relative',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#FF0000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',

                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,0,0,0.6)',
                      animation: 'pulse 1.8s ease-out infinite',
                    },

                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)', opacity: 0.8 },
                      '100%': { transform: 'scale(1.8)', opacity: 0 },
                    },
                  }}
                >
                  <YouTubeIcon sx={{ color: '#fff', fontSize: 28 }} />
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: 16, sm: 17 },
                    fontWeight: 600,
                    textDecoration: 'underline',
                    '&:hover': { color: '#1976d2' },
                  }}
                >
                  how to create paystubs in 3 simple steps
                </Typography>
              </Box>
            </Link>
          </Box>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
              fontWeight: 600,
              mb: 1,
              textAlign: 'left',
            }}
          >
            Why Choose Our Paystub Generator?
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
          >
            <Link
              href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
              target="_blank"
              sx={{ color: '#0202ff' }}
            >
              SecurePayStubs
            </Link>{' '}
            sets itself apart from conventional paystub generators by automating
            and accurately calculating all relevant taxes for paystubs. This
            includes federal taxes as well as those for all 50 states. Simply
            input your company, employee, and salary details to ensure precise
            tax calculations without any hassle.
          </Typography>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Grid
          container
          spacing={2}
          m={2}
          mt={0}
          className="MuiGrid-root-width"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Grid size={12}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
              }}
            >
              What feature differs from other online paystub generators?
            </Typography>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          m={2}
          className="MuiGrid-root-width"
          sx={{
            flexDirection: {
              lg: 'row',
              md: 'row',
              sm: 'row',
              xs: 'column',
            },
            alignItems: 'center',
            justifyContent: 'center',
          }}
          justifyContent="space-evenly"
        >
          <Grid size={{ xs: 12, md: 6.3, sm: 6 }}>
            <picture>
              <source srcSet="/assets/images/Free-paystubs.webp" />
              <source srcSet="/assets/images/Free-paystubs.png" />
              <img
                src="/assets/images/customize-paystub.png"
                alt="Customize paystubs"
                className="img-max"
                loading="lazy"
                width={525}
                height={550}
              />
            </picture>
          </Grid>
          <Grid
            size={{ xs: 12, md: 5.7, sm: 6 }}
            sx={{
              textAlign: {
                lg: 'left',
                md: 'left',
                sm: 'left',
                xs: 'center',
              },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 1,
              }}
            >
              Free Paystub Generator
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Get started without any cost and experience the simplicity of
              creating paystubs online:
            </Typography>
            <List
              sx={{
                width: '100%',
                textAlign: 'center',
                py: 0,
              }}
            >
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Generate your first paystub absolutely free, with no hidden
                  charges.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Access all essential features to create a complete and
                  accurate paystub in minutes.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Perfect for employees, freelancers, and small business owners
                  who want to try before committing.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Download or preview your free paystub instantly to check
                  accuracy and formatting.
                </ListItemText>
              </ListItem>
            </List>
          </Grid>
        </Grid>
        <Divider sx={{}} />
        <Grid
          container
          spacing={2}
          m={2}
          className="MuiGrid-root-width"
          sx={{
            flexDirection: {
              lg: 'row',
              md: 'row',
              sm: 'row',
              xs: 'column-reverse',
            },
            alignItems: 'center',
            justifyContent: 'center',
          }}
          justifyContent="space-evenly"
        >
          <Grid
            size={{ xs: 12, md: 5.7, sm: 6 }}
            sx={{
              textAlign: {
                lg: 'left',
                md: 'left',
                sm: 'left',
                xs: 'center',
              },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 1,
              }}
            >
              Accurate tax calculations
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Our paystub generator ensures Accurate tax calculations,
              including:
            </Typography>
            <List
              sx={{
                width: '100%',
                textAlign: 'center',
                py: 0,
              }}
            >
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Federal taxes, covering federal income tax and FICA taxes.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  State taxes across all 50{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}state-paystub-generator/?ref=${referralUrl}`}
                    target="_blank"
                    sx={{
                      color: '#0202ff',
                    }}
                  >
                    states
                  </Link>
                  , including income tax and disability insurance.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Local income taxes, ensuring accurate deductions based on your
                  location.
                </ListItemText>
              </ListItem>
            </List>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6.3 }}>
            <picture>
              <source srcSet="/assets/images/accurate-tax.webp" />
              <source srcSet="/assets/images/accurate-tax.png" />
              <img
                src="/assets/images/accurate-tax.png"
                alt="Accurate tax calculations"
                className="img-max"
                loading="lazy"
                width={525}
                height={550}
              />
            </picture>
          </Grid>
        </Grid>
        <Divider sx={{}} />
        <Grid
          container
          spacing={2}
          m={2}
          className="MuiGrid-root-width"
          sx={{
            flexDirection: {
              lg: 'row',
              md: 'row',
              sm: 'row',
              xs: 'column',
            },
            alignItems: 'center',
            justifyContent: 'center',
          }}
          justifyContent="space-evenly"
        >
          <Grid size={{ xs: 12, sm: 6, md: 6.3 }}>
            <picture>
              <source srcSet="/assets/images/customize-paystub.webp" />
              <source srcSet="/assets/images/customize-paystub.png" />
              <img
                src="/assets/images/customize-paystub.png"
                alt="Customize paystubs"
                className="img-max"
                loading="lazy"
                width={525}
                height={550}
              />
            </picture>
          </Grid>
          <Grid
            size={{ xs: 12, md: 5.7, sm: 6 }}
            sx={{
              textAlign: {
                lg: 'left',
                md: 'left',
                sm: 'left',
                xs: 'center',
              },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 1,
              }}
            >
              Customize paystubs
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Create professional paystubs that match your needs:
            </Typography>
            <List
              sx={{
                width: '100%',
                textAlign: 'center',
                py: 0,
              }}
            >
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Select from a variety of{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}paystub-templates/?ref=${referralUrl}`}
                    target="_blank"
                    sx={{
                      color: '#0202ff',
                    }}
                  >
                    paystub templates
                  </Link>{' '}
                  to suit your preference.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Personalize your paystub by adding your business logo for a
                  professional touch.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Easily include essential details such as check number, deposit
                  slip, and time-off information to ensure comprehensive
                  documentation.
                </ListItemText>
              </ListItem>
            </List>
          </Grid>
        </Grid>
        <Divider sx={{}} />
        <Grid
          container
          spacing={2}
          m={2}
          className="MuiGrid-root-width"
          sx={{
            flexDirection: {
              lg: 'row',
              md: 'row',
              sm: 'row',
              xs: 'column-reverse',
            },
            alignItems: 'center',
            justifyContent: 'center',
          }}
          justifyContent="space-evenly"
        >
          <Grid
            size={{ xs: 12, sm: 6, md: 5.7 }}
            sx={{
              textAlign: {
                lg: 'left',
                md: 'left',
                sm: 'left',
                xs: 'center',
              },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 1,
              }}
            >
              Earnings and deductions
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Easily tailor paystubs with detailed earnings and deductions:
            </Typography>
            <List
              sx={{
                width: '100%',
                textAlign: 'center',
                py: 0,
              }}
            >
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Easily incorporate additional earnings like overtime, tips,
                  bonuses, and more.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Seamlessly include deductions such as 401(k) contributions,
                  insurance premiums, FSAs, and other benefits.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon
                  sx={{
                    minWidth: 'inherit',
                    mr: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: '#333',
                      fontSize: 30,
                    }}
                  />
                </ListItemIcon>
                <ListItemText>
                  Enjoy the flexibility of adding custom{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}paycheck-deductions-earnings/?ref=${referralUrl}`}
                    target="_blank"
                    sx={{
                      color: '#0202ff',
                    }}
                  >
                    earnings and deductions
                  </Link>{' '}
                  to tailor paystubs to specific needs and preferences.
                </ListItemText>
              </ListItem>
            </List>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6.3 }}>
            <picture>
              <source srcSet="/assets/images/earnings-deduction.webp" />
              <source srcSet="/assets/images/earnings-deduction.png" />
              <img
                src="/assets/images/earnings-deduction.png"
                alt="Earnings and deductions"
                className="img-max"
                loading="lazy"
                width={525}
                height={550}
              />
            </picture>
          </Grid>
        </Grid>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
              fontWeight: 600,
              mb: 1,
              textAlign: 'left',
            }}
          >
            Who is SecurePayStubs?
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
          >
            SecurePayStubs is an{' '}
            <Link
              href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
              target="_blank"
              sx={{ color: '#0202ff' }}
            >
              online paystub generator
            </Link>{' '}
            that helps small businesses to create pay stubs for their employees
            and contractors accurately and instantly. With our extensive
            features, you can create a pay stub with accurate taxes for federal
            and all the 50 states.{' '}
            <Link
              href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}securepaystubs-for-taxzerone-users/?ref=${referralUrl}`}
              target="_blank"
              sx={{ color: '#0202ff' }}
            >
              SecurePayStubs
            </Link>{' '}
            is the sister product of TaxZerone.
          </Typography>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          py: 0,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ md: 6, xs: 12 }} textAlign="right">
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <picture>
                  <source
                    srcSet="/assets/images/sps-mobile-app-img.webp"
                    className="img-responsive"
                  />
                  <source
                    srcSet="/assets/images/sps-mobile-app-img.png"
                    className="img-responsive"
                  />
                  <img
                    src="/assets/images/sps-mobile-app-img.png"
                    alt="SecurePayStubs is also available as a mobile app"
                    className="img-responsive"
                    loading="lazy"
                    width={657}
                    height={770}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 0 }, mt: { xs: -3, md: 0 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  SecurePayStubs is also available as a mobile app
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 3,
                  }}
                >
                  Easily generate and email accurate paystubs for employees and
                  independent contractors on the go. Our{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}mobile-apps/?ref=${referralUrl}`}
                    target="_blank"
                    sx={{
                      color: '#0202ff',
                    }}
                  >
                    mobile app
                  </Link>{' '}
                  supports both iOS and Android devices.
                </Typography>

                <Typography
                  variant="body1"
                  color="secondary"
                  sx={{ fontWeight: 600 }}
                >
                  Get started now by installing the app!
                </Typography>
                <Box
                  sx={{
                    my: 1,
                    mb: 0,
                    '& img': {
                      width: { xs: 120, sm: 180 },
                      height: { xs: 37, sm: 55 },
                    },
                  }}
                >
                  <Link
                    href="https://play.google.com/store/apps/details?id=com.securepaystubs"
                    target="_blank"
                    sx={{
                      mr: { xs: 1, sm: 1 },
                      my: { xs: 0, sm: 0 },
                      display: 'inline-block',
                    }}
                  >
                    <Tooltip title="Available on the Google Play" arrow>
                      <picture>
                        <source srcSet="/assets/images/play-store-img.webp" />
                        <source srcSet="/assets/images/play-store-img.png" />
                        <img
                          src="/assets/images/play-store-img.png"
                          alt="Available on the Google Play"
                          width={180}
                          height={55}
                        />
                      </picture>
                    </Tooltip>
                  </Link>
                  <Link
                    href="https://apps.apple.com/us/app/paycheck-paystub-generator/id1666526433"
                    target="_blank"
                    sx={{
                      ml: { xs: 1, sm: 1 },
                      my: { xs: 0, sm: 0 },
                      display: 'inline-block',
                    }}
                  >
                    <Tooltip title="Download on the App Store" arrow>
                      <picture>
                        <source srcSet="/assets/images/app-store-img.webp" />
                        <source srcSet="/assets/images/app-store-img.png" />
                        <img
                          src="/assets/images/app-store-img.png"
                          alt="Download on the App Store"
                          width={180}
                          height={55}
                        />
                      </picture>
                    </Tooltip>
                  </Link>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          mb: 0,
        }}
        className="curve-bg"
      >
        <Container sx={{ maxWidth: '1000px !important' }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                color="white"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  lineHeight: 1.5,
                }}
              >
                Experience the simplicity and professionalism of our online
                paystub generator, SecurePayStubs.
              </Typography>

              <Typography
                variant="h3"
                sx={{
                  fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                  fontWeight: 400,
                  color: '#f3f3f3',
                  mb: 5,
                }}
              >
                Create accurate tax information effortlessly now!
              </Typography>

              <Box component="div" sx={{ margin: 'auto' }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: { xs: 0, sm: 0 },
                    mb: { xs: 0, md: 0 },
                    color: '#444',
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &active': {
                      backgroundColor: '#213e99 !important',
                      color: '#fff',
                      transform: 'scale(1.1)',
                    },
                  }}
                  className="btn-secondary-white"
                  href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
                  target="_blank"
                >
                  Generate PayStub Now
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              FAQs
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What information do I need to generate a paystub?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    To generate a{' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      paystub
                    </Link>
                    , you typically need the employee&apos;s personal details
                    like name, address, and Social Security number, along with
                    earnings information such as pay rate, hours worked,
                    bonuses, and deductions like taxes and insurance premiums.
                    Additionally, details about the employer and pay period may
                    be
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Can I create a pay stub for free?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Yes, you can create your first paystub for free with code:
                    FIRSTSTUB with our{' '}
                    <Link
                      href="https://www.freefirststub.com/?ref=taxzerone"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      free paystub generator
                    </Link>
                    . Input your information, preview, apply the code, and
                    effortlessly download your accurate pay stub at no cost.
                    Precise calculations based on your input are effortlessly
                    done.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What are the best paystub generators?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    There are several reputable{' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      paystub generators
                    </Link>{' '}
                    available, and one popular option is SecurePayStubs.
                    SecurePayStubs offers a user-friendly interface and
                    <Link
                      href="https://support.securepaystubs.com/what-do-securepaystubs-templates-offer/?ref=taxzerone"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      customizable templates
                    </Link>
                    , making it easy to generate accurate paystubs quickly. It
                    provides a secure platform to input essential employee and
                    employer information and offers features like automatic tax
                    calculations and compliance with relevant regulations. With
                    SecurePayStubs, users can create professional-looking
                    paystubs with ease, making it a top choice for businesses
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. Is it legal to use a paystub generator?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography>
                      Using a paystub generator is legal if it accurately
                      reflects wages and complies with laws. However, creating
                      fake{' '}
                      <Link
                        href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        paystubs
                      </Link>{' '}
                      for fraud is illegal. Make sure your paystubs are accurate
                      and lawful, and seek legal advice if needed.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. Who needs a paystub?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography>
                      Paystubs are essential for employees to track earnings,
                      provide proof of income for loans or renting, and for tax
                      purposes. Employers benefit from providing paystubs for
                      transparency and resolving payment discrepancies.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                  itemProp="name"
                >
                  6. Why should I use an online paystub generator?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mb: 2 }}>
                      Using an{' '}
                      <Link
                        href="https://www.securepaystubs.com/?ref=taxzerone"
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        online paystub generator
                      </Link>{' '}
                      offers several advantages:
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                      <b>Convenience:</b> Online paystub generators allow you to
                      create pay stubs anytime, anywhere, as long as you have
                      internet access. This is especially useful for
                      freelancers, small business owners, or employees who work
                      remotely.
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                      <b>Time-saving:</b> Generating pay stubs manually can be
                      time-consuming, especially if you have multiple employees
                      or need to create pay stubs frequently. Online generators
                      automate this process, saving you time and effort.
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                      <b>Accuracy:</b> Paystub generators typically have
                      built-in calculations for taxes, deductions, and other
                      payroll components. This reduces the risk of errors
                      compared to manual calculations, ensuring that your pay
                      stubs are accurate and compliant with relevant
                      regulations.
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                      <b>Customization:</b> Many online paystub generators allow
                      you to customize the pay stubs according to your needs.
                      You can include company logos, employee details, specific
                      deductions, and other relevant information to make the pay
                      stubs more professional and personalized.
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                      <b>Cost-effective:</b> Using an online{' '}
                      <Link
                        href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        paystub generator
                      </Link>{' '}
                      can be more{' '}
                      <Link
                        href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}pricing/?ref=${referralUrl}`}
                        target="_blank"
                        sx={{
                          color: '#0202ff',
                        }}
                      >
                        cost-effective
                      </Link>{' '}
                      than hiring an accountant or purchasing expensive payroll
                      software, especially for small businesses or individuals
                      with limited payroll needs.
                    </Typography>

                    <Typography sx={{}}>
                      Overall, using an online paystub generator streamlines the
                      payroll process, saves time and effort, and helps ensure
                      accuracy and compliance with payroll regulations.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  7. What does Year to Date (YTD) mean and why is it included in
                  the paystub?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mb: 2 }}>
                      <Link
                        href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}resources/year-to-date/?ref=${referralUrl}`}
                        target="_blank"
                        sx={{
                          color: '#0202ff',
                        }}
                      >
                        Year to Date (YTD)
                      </Link>{' '}
                      refers to the period from the beginning of the current
                      calendar year up to the present date. In the context of a{' '}
                      <Link
                        href={`${process.env.NEXT_PUBLIC_SPS_HOMESITE_URL}?ref=${referralUrl}`}
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        paystub
                      </Link>
                      , YTD typically refers to various earnings, deductions,
                      and taxes withheld from an employee&apos;s pay since the
                      beginning of the year.
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                      YTD information is included on a paystub for several
                      reasons:
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                      <b>Track of Total Earnings:</b> It allows employees to see
                      how much they have earned from the start of the year until
                      the current pay period. This includes wages, salary,
                      bonuses, commissions, and any other forms of compensation.
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                      <b>Understanding Deductions:</b> YTD deductions show the
                      total amount withheld from an employee&apos;s pay for
                      taxes, retirement contributions, insurance premiums, and
                      other deductions. This helps employees understand their
                      net pay and how much has been deducted over the course of
                      the year.
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                      <b>Tax Reporting:</b> YTD figures are crucial for tax
                      reporting purposes. Employers use this information to
                      accurately report employees&apos; earnings and deductions
                      to tax authorities, such as the Internal Revenue Service
                      (IRS) in the United States. YTD data ensures that
                      employees&apos; tax withholdings are accurate and helps in
                      the preparation of annual tax returns.
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                      <b>Budgeting and Financial Planning:</b> YTD information
                      provides employees with insights into their overall
                      financial situation for the year. By seeing how much
                      they&apos;ve earned and how much has been deducted over
                      time, employees can better plan their finances and make
                      informed decisions about budgeting, saving, and spending.
                    </Typography>
                    <Typography sx={{}}>
                      Overall, YTD figures on a paystub serve as a snapshot of
                      an employee&apos;s financial performance and help both
                      employees and employers maintain accurate records and
                      comply with tax regulations.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default PayStubGenerator;
